import { html, css } from 'lit'
import { ModalBaseMixin } from './modal-base.mixin.js'
import { modalStackRegistry } from '../registries/modal-stack-registry.js'

/**
 * Mixin que encapsula o comportamento de salvamento com loading,
 * erro inline e integração com cache service.
 *
 * Estende o ModalBaseMixin adicionando:
 * - Estado reativo `_saving` para controle de loading no botão Salvar
 * - Estado reativo `_errorMessage` para exibição de erro inline
 * - Método `_executeSave()` com timeout de 30s via Promise.race
 * - Guard no `close()` que impede fechamento durante salvamento
 * - Eventos `cache-operation-started` e `cache-operation-finished` via window
 * - Override do handler de popstate para re-push do history state durante saving
 * - Cleanup de timeouts no disconnectedCallback
 *
 * Subclasses devem definir a propriedade `_entidade` (string) para identificar
 * a entidade do modal nos eventos e no atributo data-cy de erro.
 *
 * @param {typeof import('lit').LitElement} superClass - Classe base Lit Element
 * @returns {typeof import('lit').LitElement} Classe com comportamento de salvamento
 *
 * @example
 * import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
 *
 * class ModalConta extends ModalSaveMixin(LitElement) {
 *   _entidade = 'conta'
 *
 *   async _handleSave() {
 *     const dados = this._coletarDados()
 *     const operacao = this.isEditMode ? 'atualizar' : 'criar'
 *     const id = this.isEditMode ? this._id : undefined
 *     await this._executeSave(contaCacheService, operacao, dados, id)
 *   }
 * }
 */
export const ModalSaveMixin = (superClass) => class ModalSave extends ModalBaseMixin(superClass) {
  static properties = {
    ...super.properties,
    _saving: { type: Boolean, state: true },
    _errorMessage: { type: String, state: true }
  }

  /**
   * ID do timeout de 30s para controle de operações longas.
   * Armazenado para permitir cleanup no disconnectedCallback e close().
   *
   * @type {number|null}
   */
  _saveTimeoutId = null

  /**
   * Nome da entidade do modal (ex: 'conta', 'categoria', 'tag').
   * Deve ser definido pela subclasse. Usado nos eventos e no data-cy de erro.
   *
   * @type {string}
   */
  _entidade = ''

  constructor () {
    super()
    this._saving = false
    this._errorMessage = null
  }

  /**
   * Executa operação de salvamento com loading, timeout e tratamento de erro.
   *
   * Fluxo:
   * 1. Verifica guard contra clique duplo (_saving = true → retorna)
   * 2. Limpa erro anterior e ativa estado de loading
   * 3. Dispara evento `cache-operation-started` via window
   * 4. Executa operação no cache service com timeout de 30s via Promise.race
   * 5. Em caso de sucesso: dispara `cache-operation-finished` e fecha o modal
   * 6. Em caso de erro: dispara `cache-operation-finished` e exibe erro inline
   *
   * @param {Object} cacheService - Instância do cache service da entidade
   * @param {'criar'|'atualizar'|'excluir'} operacao - Tipo de operação CRUD
   * @param {Object} dados - Dados do formulário para envio
   * @param {string} [id] - ID da entidade (obrigatório para atualizar/excluir)
   * @returns {Promise<Object|undefined>} Entidade retornada pelo backend ou undefined em caso de erro
   */
  async _executeSave (cacheService, operacao, dados, id) {
    // Guard contra clique duplo — ignora se já está salvando
    if (this._saving) return

    // Limpa erro anterior e ativa loading
    this._clearError()
    this._saving = true

    // Dispara evento de início da operação para páginas ouvintes (skeleton)
    window.dispatchEvent(new CustomEvent('cache-operation-started', {
      detail: { entidade: this._entidade, operacao }
    }))

    try {
      // Monta a chamada ao cache service conforme operação
      const operationPromise = this._buildOperationPromise(cacheService, operacao, dados, id)

      // Timeout de 30s via Promise.race
      const timeoutPromise = new Promise((_, reject) => {
        this._saveTimeoutId = setTimeout(() => {
          reject(new Error('Operação expirou. Tente novamente.'))
        }, 30000)
      })

      const result = await Promise.race([operationPromise, timeoutPromise])

      // Limpa timeout após sucesso
      this._clearSaveTimeout()

      // Dispara evento de conclusão com sucesso
      window.dispatchEvent(new CustomEvent('cache-operation-finished', {
        detail: { entidade: this._entidade, sucesso: true }
      }))

      // Restaura _saving antes de fechar para que o guard de close() permita o fechamento
      this._saving = false

      // Fecha o modal após sucesso
      this.close()

      return result
    } catch (error) {
      // Limpa timeout após erro
      this._clearSaveTimeout()

      // Dispara evento de conclusão com falha
      window.dispatchEvent(new CustomEvent('cache-operation-finished', {
        detail: { entidade: this._entidade, sucesso: false }
      }))

      // Restaura estado e exibe erro inline
      this._saving = false
      this._handleSaveError(error)
    }
  }

  /**
   * Constrói a Promise da operação CRUD no cache service.
   *
   * Roteia para o método correto do cache service com base no tipo de operação:
   * - 'criar': cacheService.criar(dados)
   * - 'atualizar': cacheService.atualizar(id, dados)
   * - 'excluir': cacheService.excluir(id)
   *
   * @param {Object} cacheService - Instância do cache service
   * @param {'criar'|'atualizar'|'excluir'} operacao - Tipo de operação
   * @param {Object} dados - Dados do formulário
   * @param {string} [id] - ID da entidade
   * @returns {Promise<Object>} Promise da operação
   */
  _buildOperationPromise (cacheService, operacao, dados, id) {
    switch (operacao) {
      case 'criar':
        return cacheService.criar(dados)
      case 'atualizar':
        return cacheService.atualizar(id, dados)
      case 'excluir':
        return cacheService.excluir(id)
      default:
        return Promise.reject(new Error(`Operação desconhecida: ${operacao}`))
    }
  }

  /**
   * Trata erro de salvamento exibindo mensagem inline no modal.
   *
   * Regras de truncamento:
   * - Se error.message existe e tem <= 150 chars: exibe integralmente
   * - Se error.message existe e tem > 150 chars: trunca com "..."
   * - Se error.message não existe ou está vazia: exibe mensagem genérica
   *
   * @param {Error} error - Erro capturado da operação
   */
  _handleSaveError (error) {
    const message = error && error.message ? error.message : ''

    if (!message) {
      this._errorMessage = 'Erro ao salvar. Tente novamente.'
      return
    }

    if (message.length > 150) {
      this._errorMessage = message.substring(0, 150) + '...'
      return
    }

    this._errorMessage = message
  }

  /**
   * Limpa a mensagem de erro inline.
   *
   * Chamado quando o usuário interage com campos do formulário (input/change)
   * ou quando uma nova tentativa de salvamento é iniciada.
   */
  _clearError () {
    this._errorMessage = null
  }

  /**
   * Limpa o timeout de salvamento pendente.
   *
   * Chamado após sucesso, erro ou no disconnectedCallback para evitar
   * execução de timeout órfão após o componente ser removido do DOM.
   */
  _clearSaveTimeout () {
    if (this._saveTimeoutId !== null) {
      clearTimeout(this._saveTimeoutId)
      this._saveTimeoutId = null
    }
  }

  /**
   * Sobrescreve close() do ModalBaseMixin com guard contra _saving.
   *
   * Quando _saving = true:
   * - Retorna imediatamente sem fechar o modal
   * - Não dispara evento modal-closed
   * - Não altera a propriedade open
   *
   * Quando _saving = false:
   * - Reseta estado de saving e erro antes de fechar
   * - Limpa timeout pendente
   * - Delega ao super.close() para comportamento padrão
   */
  close (options = {}) {
    // Guard: impede fechamento durante salvamento
    if (this._saving) return

    // Reset de estado antes de fechar
    this._saving = false
    this._errorMessage = null
    this._clearSaveTimeout()

    super.close(options)
  }

  /**
   * Registra o handler de popstate com override para re-push durante saving.
   *
   * Quando _saving = true e o usuário aciona o botão voltar do navegador:
   * - Re-executa pushState para restaurar a entrada de histórico consumida
   * - Mantém consistência entre modal aberto e history stack
   *
   * Quando _saving = false:
   * - Delega ao comportamento padrão (fecha o modal)
   */
  _registerPopstateHandler () {
    if (this._boundPopstateHandler) return

    this._boundPopstateHandler = () => {
      // Guard 1: Ignora se outro modal já consumiu este popstate no mesmo tick
      // Previne que múltiplos modais respondam ao mesmo evento popstate (Req 2.3, 2.4)
      if (modalStackRegistry.isPopstateConsumed()) return

      // Guard 1b: Ignora popstate gerado pela limpeza de histórico de outro modal
      if (modalStackRegistry._cleanupInProgress) {
        modalStackRegistry._cleanupInProgress = false
        return
      }

      if (this._saving) {
        // Re-push do history state para manter modal aberto
        window.history.pushState({ modal: true }, '')
        return
      }

      // Guard 2: Ignora se modal já está fechado
      if (!this.open) return

      // Guard 3: Apenas o modal do topo da stack responde ao popstate
      if (!modalStackRegistry.isTop(this)) return

      // Marca o popstate como consumido ANTES de fechar
      modalStackRegistry.consumePopstate()

      this.close({ fromPopstate: true })
    }

    window.addEventListener('popstate', this._boundPopstateHandler)
  }

  /**
   * Lifecycle hook de desconexão do DOM.
   *
   * Além do cleanup do ModalBaseMixin (handler de popstate),
   * limpa o timeout de salvamento pendente para evitar memory leaks.
   */
  disconnectedCallback () {
    this._clearSaveTimeout()
    super.disconnectedCallback()
  }

  /**
   * Renderiza a mensagem de erro inline no corpo do modal.
   *
   * Posicionada abaixo dos campos do formulário e acima do footer.
   * Usa classe Bulma `notification is-danger is-light` com tokens CSS
   * do design system para cores de texto e fundo.
   *
   * @returns {import('lit').TemplateResult} Template do erro ou vazio
   */
  renderErroSalvar () {
    if (!this._errorMessage) return html``

    return html`
      <div
        class="notification is-danger is-light"
        data-cy="erro-salvar-${this._entidade}"
        style="color: var(--text-button-danger-light, #EF4444);
               background-color: var(--bg-button-danger-light, rgba(220, 53, 69, 0.2));
               margin-top: 0.75rem;
               margin-bottom: 0;"
      >
        ${this._errorMessage}
      </div>
    `
  }

  /**
   * Estilos CSS do ModalSaveMixin.
   *
   * Inclui estilos do ModalBaseMixin e adiciona estilos para
   * o estado de loading e mensagem de erro inline.
   *
   * @returns {import('lit').CSSResult} Estilos CSS combinados
   */
  static get modalSaveStyles () {
    return css`
      ${ModalBaseMixin(superClass).modalBaseStyles}

      .modal-save-error {
        color: var(--text-button-danger-light, #EF4444);
        background-color: var(--bg-button-danger-light, rgba(220, 53, 69, 0.2));
        margin-top: 0.75rem;
        margin-bottom: 0;
      }
    `
  }
}
